import React, { CSSProperties, useEffect, useState } from 'react'
import { useNearViewport } from 'common/useNearViewport'

interface SupademoProps {
  showcaseId: string
  /** Aspect ratio as width/height. Default is 16/9. */
  aspectRatio?: number
}

const isDemoHash = () =>
  typeof window !== 'undefined' && window.location.hash === '#demo'

const Supademo: React.FC<SupademoProps> = ({
  aspectRatio = 16 / 9,
  showcaseId,
}) => {
  const [ref, isNear] = useNearViewport<HTMLDivElement>()
  const [forceLoad, setForceLoad] = useState(false)

  useEffect(() => {
    if (!isDemoHash()) return
    setForceLoad(true)

    // Content above (images, fonts) can shift layout after first paint, so
    // re-anchor a few times until either the user scrolls or things settle.
    let cancelled = false
    const onUserScroll = () => {
      cancelled = true
    }
    const scrollToDemo = () => {
      if (cancelled) return
      document
        .getElementById('demo')
        ?.scrollIntoView({ behavior: 'auto', block: 'start' })
    }

    const timers = [0, 250, 800, 2000].map((delay) =>
      window.setTimeout(scrollToDemo, delay),
    )
    window.addEventListener('load', scrollToDemo)
    window.addEventListener('wheel', onUserScroll, { passive: true })
    window.addEventListener('touchmove', onUserScroll, { passive: true })
    window.addEventListener('keydown', onUserScroll)

    return () => {
      timers.forEach((id) => window.clearTimeout(id))
      window.removeEventListener('load', scrollToDemo)
      window.removeEventListener('wheel', onUserScroll)
      window.removeEventListener('touchmove', onUserScroll)
      window.removeEventListener('keydown', onUserScroll)
    }
  }, [])

  // padding-bottom is percentage of width, so height = (1 / aspectRatio) * 100%
  const paddingBottom = `${100 / aspectRatio}%`

  const containerStyle: CSSProperties = {
    paddingBottom,
    position: 'relative',
    width: '100%',
  }

  const iframeStyle: CSSProperties = {
    border: 'none',
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  }

  return (
    <div className=''>
      <div className='text-center d-none fw-semibold d-safari-block fs-fluid-22-32'>
        For the best experience, please view this demo in full screen.
      </div>
      <div
        id='demo'
        ref={ref}
        className='rounded-xl mt-8 d-none d-md-block shadow overflow-hidden'
        style={containerStyle}
      >
        {(isNear || forceLoad) && (
          <iframe
            src={`https://app.supademo.com/showcase/${showcaseId}?embed=true`}
            style={iframeStyle}
            allowFullScreen
          />
        )}
      </div>
    </div>
  )
}

export default Supademo
